import { storageBoxApi } from '../api/storageBoxApi';
import { decryptToBlob, decryptToText, decryptLockChunk } from './storageBoxLockCrypto';
import {
  cipherByteOffsetForChunk,
  cipherBytesInChunk,
  isChunkedLockEncFormat,
  lockChunkCount,
  lockChunkPlainSize,
} from './storageBoxLockChunkFormat';
import { fetchLockCipherBytes, fetchLockCipherFullBuffer } from './lockFileCipherFetch';

export async function fetchAndDecryptLockFile(item, lockSessionToken, masterKeyBytes) {
  const mime = item.mimeType || '';
  if (isChunkedLockEncFormat(item)) {
    const plainSize = Number(item.sizeBytes) || 0;
    const chunkPlain = lockChunkPlainSize(item);
    const chunks = lockChunkCount(plainSize, chunkPlain);
    const plainParts = [];
    for (let i = 0; i < chunks; i += 1) {
      const cipherLen = cipherBytesInChunk(i, plainSize, chunkPlain);
      const start = cipherByteOffsetForChunk(i, plainSize, chunkPlain);
      const { buffer } = await fetchLockCipherBytes(item.itemId, lockSessionToken, {
        start,
        end: start + cipherLen - 1,
      });
      const plain = await decryptLockChunk(buffer, item.encIv, i, masterKeyBytes);
      plainParts.push(new Uint8Array(plain));
    }
    const blob = new Blob(plainParts, { type: mime || 'application/octet-stream' });
    return { blob, mimeType: mime };
  }

  const pack = await fetchLockCipherFullBuffer(item.itemId, lockSessionToken);
  const iv = pack.encIv || item.encIv;
  const tag = pack.encAuthTag || item.encAuthTag;
  const blob = await decryptToBlob(pack.buffer, iv, tag, masterKeyBytes, pack.mimeType || mime);
  return { blob, mimeType: pack.mimeType || mime };
}

export async function fetchAndDecryptLockText(item, lockSessionToken, masterKeyBytes) {
  const pack = await storageBoxApi.lockPreviewTextEncrypted(item.itemId, lockSessionToken);
  const iv = pack.encIv || item.encIv;
  const tag = pack.encAuthTag || item.encAuthTag;
  const ct = Uint8Array.from(atob(pack.ciphertextB64), (c) => c.charCodeAt(0));
  return decryptToText(ct.buffer, iv, tag, masterKeyBytes);
}

export function revokeObjectUrl(url) {
  if (url && typeof url === 'string' && url.startsWith('blob:')) {
    try {
      URL.revokeObjectURL(url);
    } catch {
      /* ignore */
    }
  }
}

function waitSourceBuffer(sb) {
  if (!sb.updating) return Promise.resolve();
  return new Promise((resolve) => {
    sb.addEventListener('updateend', resolve, { once: true });
  });
}

function videoSourceBufferMime(mimeType, fileName) {
  const m = (mimeType || '').toLowerCase();
  const name = (fileName || '').toLowerCase();
  if (m.includes('webm') || name.endsWith('.webm')) {
    return 'video/webm; codecs="vp8, vorbis"';
  }
  return 'video/mp4; codecs="avc1.42E01E, mp4a.40.2"';
}

/**
 * Decrypt a chunked lock video incrementally into MediaSource (one cipher chunk in RAM at a time).
 */
export async function attachChunkedLockVideoToMediaSource(
  videoEl,
  item,
  lockSessionToken,
  masterKeyBytes,
  { isCancelled = () => false } = {}
) {
  if (!videoEl || !item?.itemId) return;
  const plainSize = Number(item.sizeBytes) || 0;
  const chunkPlain = lockChunkPlainSize(item);
  const chunks = lockChunkCount(plainSize, chunkPlain);
  const mediaSource = new MediaSource();
  const objectUrl = URL.createObjectURL(mediaSource);
  videoEl.src = objectUrl;

  await new Promise((resolve, reject) => {
    mediaSource.addEventListener('sourceopen', resolve, { once: true });
    mediaSource.addEventListener('error', () => reject(new Error('Media source error')), { once: true });
  });
  if (isCancelled()) {
    URL.revokeObjectURL(objectUrl);
    return () => {};
  }

  const mime = videoSourceBufferMime(item.mimeType, item.name);
  const sourceBuffer = mediaSource.addSourceBuffer(mime);

  for (let i = 0; i < chunks; i += 1) {
    if (isCancelled()) break;
    const cipherLen = cipherBytesInChunk(i, plainSize, chunkPlain);
    const start = cipherByteOffsetForChunk(i, plainSize, chunkPlain);
    const { buffer } = await fetchLockCipherBytes(item.itemId, lockSessionToken, {
      start,
      end: start + cipherLen - 1,
    });
    const plain = await decryptLockChunk(buffer, item.encIv, i, masterKeyBytes);
    await waitSourceBuffer(sourceBuffer);
    sourceBuffer.appendBuffer(new Uint8Array(plain));
  }

  if (!isCancelled() && mediaSource.readyState === 'open') {
    try {
      mediaSource.endOfStream();
    } catch {
      /* ignore */
    }
  }

  return () => {
    URL.revokeObjectURL(objectUrl);
    try {
      if (mediaSource.readyState === 'open') mediaSource.endOfStream();
    } catch {
      /* ignore */
    }
  };
}
